import { useGetCorporationByCompany } from '@commenda/api';
import { useFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import { Icons, Loading } from '@commenda/kakashi';
import { DSLFormRenderer } from '@commenda/ui/form-builder/hooks/useFormRenderer';
import { collectRequiredContextKeys } from '@commenda/ui/form-builder/utils/context-requirements';
import { useMemo, useState, type ComponentProps } from 'react';

import { AffiliateMemberPicker } from '../affiliate/form-builder';
import AffiliateClientPicker from './AffiliateClientPicker';
import { useAffiliateAiClient } from './useAffiliateAiClient';

type FormDSL = ComponentProps<typeof DSLFormRenderer>['dsl'];

/** A client pick carries companyId plus the corporation underneath it; the member pick carries
 * directorId. Same grouping the admin preview uses, named for the affiliate's vocabulary. */
const CONTEXT_KEYS_FROM_CLIENT: readonly string[] = [
  'companyId',
  'corporationId',
];
const MEMBER_CONTEXT_KEY = 'directorId';

interface AffiliateWorkflowFormTabProps {
  formId: string;
  onClose: () => void;
}

const FormTabMessage = ({
  title,
  detail,
}: {
  title: string;
  detail: string;
}) => (
  <div className="flex h-full flex-col items-center justify-center gap-lg bg-bg-tertiary">
    <Icons.AlertCircle className="size-12 text-text-error" />
    <h2 className="txt-large-medium text-text-primary">{title}</h2>
    <p className="txt-medium-regular text-text-tertiary">{detail}</p>
  </div>
);

/** A form step's form, previewed in place beside the canvas. Scoped to one of the firm's own
 * clients so entity and key-person fields resolve against real data; with nothing picked the
 * fields render waiting, which is how the form behaves before its workflow supplies them. */
export const AffiliateWorkflowFormTab = ({
  formId,
  onClose,
}: AffiliateWorkflowFormTabProps) => {
  const {
    template,
    isLoading,
    error: templateError,
  } = useFormsTemplate(formId);
  // Only the list is taken from the hook. The pick is held here rather than pushed to `?client=`,
  // because `setConversationParam` on this page rebuilds the query and would drop it.
  const { clients } = useAffiliateAiClient();
  const [clientId, setClientId] = useState<number | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const { data: clientCorporations } = useGetCorporationByCompany(clientId);

  const dsl = useMemo(
    () => (template?.dsl ? (template.dsl as unknown as FormDSL) : null),
    [template]
  );
  const requiredContextKeys = useMemo(
    () => (dsl ? collectRequiredContextKeys(dsl) : []),
    [dsl]
  );
  const needsClientContext = requiredContextKeys.some((key) =>
    CONTEXT_KEYS_FROM_CLIENT.includes(key)
  );
  const needsMemberContext = requiredContextKeys.includes(MEMBER_CONTEXT_KEY);

  const dslContext = useMemo(() => {
    const context: Record<string, number | string> = {};
    if (clientId !== null) {
      context.companyId = clientId;
      // Corporations are sorted by id, so the first is stable across renders. A client with
      // several would need a picker of its own; none in the sandbox has more than one.
      const corporationId = clientCorporations?.[0]?.id;
      if (corporationId !== undefined) context.corporationId = corporationId;
    }
    if (needsMemberContext && selectedMemberId !== null) {
      context.directorId = selectedMemberId;
    }
    return context;
  }, [clientId, clientCorporations, needsMemberContext, selectedMemberId]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-bg-tertiary">
        <Loading />
      </div>
    );
  }

  if (templateError || !template) {
    return (
      <FormTabMessage
        title={templateError ? 'Failed to load form' : 'Form not found'}
        detail="The form could not be found, or there was an error loading it."
      />
    );
  }

  if (!template.key) {
    return (
      <FormTabMessage
        title="Form configuration error"
        detail="This form is missing a key and cannot accept submissions."
      />
    );
  }

  if (!dsl) return null;

  return (
    <div className="h-full overflow-auto bg-bg-light">
      <div className="mx-auto w-full max-w-screen-2xl">
        <DSLFormRenderer
          // Remount on a context change so one client's entered values never leak into another's.
          key={JSON.stringify(dslContext)}
          dsl={dsl}
          dslContext={dslContext}
          formTemplateId={template.id}
          formTemplateKey={template.key}
          onSubmitSuccess={onClose}
          onSaveSuccess={onClose}
          onClose={onClose}
          header={
            needsClientContext || needsMemberContext
              ? {
                  leftSlot: (
                    <div className="flex items-center gap-md">
                      <AffiliateClientPicker
                        clients={clients}
                        clientId={clientId}
                        onClientChange={(nextClientId) => {
                          setClientId(nextClientId);
                          // Members belong to a client, so a client change invalidates the pick.
                          setSelectedMemberId(null);
                        }}
                      />
                      {needsMemberContext && clientId !== null && (
                        <AffiliateMemberPicker
                          clientId={clientId}
                          selectedMemberId={selectedMemberId}
                          onMemberChange={setSelectedMemberId}
                        />
                      )}
                    </div>
                  ),
                }
              : undefined
          }
          isPreview
        />
      </div>
    </div>
  );
};
